// Turning an unknown thrown value into readable text.
//
// A `catch` block receives `unknown`. In this codebase that value is, in
// practice, one of four things, and each one needs different handling:
//
//   1. a real `Error` (or a subclass) — thrown by our own code and by libraries;
//   2. a plain object — PouchDB and CouchDB throw `{ status, error, reason }`,
//      and our HTTP helpers throw response-shaped objects;
//   3. a string — old code that threw a bare message;
//   4. anything else — `undefined`, a number, a rejected promise with no reason.
//
// Two traps made this file necessary, and both had shipped in real code:
//
//   * `JSON.stringify(err)` on an `Error` produces the literal two characters
//     `{}`. `Error` stores `message` and `stack` as NON-ENUMERABLE own
//     properties, and `JSON.stringify` only visits enumerable ones. So the log
//     line that was supposed to record why a payment write failed recorded
//     nothing at all.
//   * `String(err)` on a plain object produces `[object Object]`. That is the
//     exact shape CouchDB throws, so the reason we most wanted was the reason we
//     most reliably threw away.
//
// Use `describeError` for text that a caller or a user might see, and
// `describeErrorForLog` for a log line. The difference is deliberate: only the
// log version includes a stack, because a stack must never cross the wire.
//
// Three rules this file holds, each of which cost something to learn:
//
//   * It redacts credential FIELDS, by name. Both functions run every field
//     value through `REDACT_LOG_FIELDS` first — see the note on that constant.
//     This is NOT a promise that no credential can appear: redaction matches
//     field names, so it cannot see inside an `Error`'s own `message` or
//     `stack`, which are printed verbatim. HTTP clients routinely put the
//     request URL in the message, so an error from a call to a webhook URL or
//     a token-in-the-query-string endpoint still carries the secret. Handle
//     that at the call site — see the SECRETS section of `CLAUDE.md`.
//   * It NEVER throws. A log call must not be able to crash the code path it
//     is reporting on, so every field read and every stringify is guarded.
//   * It is bounded. A thrown value carrying a large HTML error page must not
//     be able to fill a log line, or become an oversized HTTP response body.
//
// A lint rule in `client/eslint.config.js`, `server/functions/eslint.config.cjs`
// and `shared/eslint.config.base.js` rejects `JSON.stringify(<caught error>)`
// and points here, so the class of bug this file fixes cannot come back.
// The written rule lives in `design-docs/typescript-patterns.md`.

/**
 * Field names whose values are credentials and must NEVER reach a log line.
 *
 * This is the SAME list the server's dispatch layer applies to every declared
 * function's params and result (`redactForLog` in
 * `server/functions/src/helpers/server-function-helpers.ts`, which imports it
 * from here so there is one list and not two). It exists because the couchLogin
 * result used to put three credentials into Cloud Logging on every single login
 * — secrets-inventory incident 2026-07-01, fixed with #3993.
 *
 * Matching is by exact field name, at any nesting depth.
 *
 * Bias toward over-matching when adding a name: redacting a field that turns
 * out not to be secret costs a little log fidelity, while missing one leaks it
 * forever. (`token` is deliberately broad for exactly that reason — it catches
 * the createCustomToken sign-in tokens returned by userSubscription and
 * exchangePurchaseClaimToken; `idToken` / `refreshToken` cover cliAuthStatus.)
 *
 * `derived_key`, `salt` and `password_scheme` are the CouchDB `_users`
 * document's password verifier (#4474); it leaked into Cloud Logging twice —
 * see design-docs/secrets-inventory.md, "Incident log".
 *
 * `secret` is the shared Cloud Scheduler auth token (#4849). Every scheduler
 * job POSTs it as a plain body field called `secret` (the provisioner builds
 * that body in tools/src/provision-schedulers.ts, and scheduler-auth.ts checks
 * it), so the dispatch layer printed it in full on every cron tick until this
 * name was added. It is a common word, which is why it is worth stating that
 * nothing else in the declared-function surface passes or returns a field by
 * that name — so redacting it costs no log fidelity anywhere.
 *
 * `sso`, `sig` and `redirectUrl` are the Discourse community login (#4525).
 * Signing into the forum goes through the declared function `ssoDiscourse`: its
 * params carry `sso`, a base64 payload naming the account, and `sig`, an HMAC
 * over that payload; its result carries `redirectUrl`, the finished forum
 * address with a fresh payload and HMAC of our own in the query string. That
 * address is a working single-use login for the named user's forum account, and
 * the dispatch layer printed it on every community login until these names went
 * on the list. Fixing the handler's own log lines did NOT close this — the
 * dispatcher logs params and result whatever the handler does, which is the
 * whole reason this list exists. As with `secret` above, nothing else in the
 * declared-function surface passes or returns a field by any of these three
 * names, so redacting them costs no log fidelity anywhere.
 *
 * `payProTestModeKey` is the PayPro test-mode key an E2E run presents to
 * `storeGetCheckout` to prove it may have a test-mode checkout
 * (df-59d822c22018). The dispatcher prints every declared function's params,
 * so without this name every E2E checkout would write the key into the server
 * log. Nothing else passes or returns a field by that name.
 *
 * `authorizationCode` is the one-time Apple Sign-In code the iOS client passes
 * to `exchangeAppleAuthCode` (#6073). It is single-use and lives about five
 * minutes, but it is a credential, and the dispatcher printed it on every Apple
 * sign-in until this name went on the list. Nothing else passes or returns a
 * field by that name.
 *
 * `apiSecretKey` is the PayPro vendor API key (`PAYPRO_APISECRETKEY`), which
 * `payproCallAPI` sends in every request body. PayPro echoes the request back
 * in its response, and logging that response wrote the live key into Cloud
 * Logging on every call (internal ticket cybsol/fitness-app#1417).
 * `payproCallAPI` now drops the echo before logging; this name is the second
 * layer, for any other place the request or its echo reaches a describer. The
 * existing `secret` entry does not cover it, because matching is by exact name.
 *
 * The names below were found by the audit in #6764. Each is a credential
 * the dispatcher printed in full on every call, and each name is used for that
 * credential only, so redacting it costs no log fidelity elsewhere:
 * - `claimToken` is a coach invite token (it never expires while its slot is
 *   pending) or a purchase claim token (valid 30 days). Both are bearer
 *   credentials. `claimUrl` is the coach invite link built around one.
 * - `androidInstallUrl` is a Play Store install link whose referrer carries a
 *   purchase claim token.
 * - `customerAuthToken` is the Paddle token that opens a checkout showing the
 *   customer's saved card and address.
 * - `purchaseToken` is the Google Play purchase credential. The subscription
 *   audit rows already mask it (#6418).
 * - `signedUrl` is a 15-minute signed write URL to one storage object.
 *
 * `invoiceUrl` is a Paddle invoice link (#6768): anyone holding it can read
 * the coach's invoice, with their name and address, for an hour. The coach
 * billing page's invoice functions return it, and both also list it in their
 * own `logRedactKeys`. The same name is used for the stored `paddleData` and
 * `payProData` invoice links, which are capability links of the same kind, so
 * blanking them in logs too costs no useful fidelity.
 *
 * Generic names that hold a credential in only one function (`url` in
 * `paddleManageSubscription`, `transactionId` in `getPaddlePurchaseClaimToken`)
 * do NOT go here. Putting them here would blank unrelated fields everywhere.
 * Instead that function declares them in its own `logRedactKeys`, and the
 * dispatcher blanks them in that function's log lines only.
 *
 * Two neighbouring fields are deliberately NOT on this list — do not "fix" the
 * apparent omission. `iterations` is a plain number (600000) that reveals
 * nothing on its own, and `pbkdf2_prf` names a hash function ('sha256'). Both
 * names are generic enough that redacting them would blank out unrelated fields
 * elsewhere in the codebase, which costs log fidelity for no security gain.
 */
export const REDACT_LOG_FIELDS: ReadonlySet<string> = new Set([
  'couchPass', 'sessionCookie', 'siwaRefreshToken', 'idToken', 'refreshToken', 'token',
  'derived_key', 'salt', 'password_scheme', 'secret',
  'sso', 'sig', 'redirectUrl',
  'payProTestModeKey',
  'authorizationCode',
  'apiSecretKey',
  'claimToken', 'claimUrl', 'androidInstallUrl', 'customerAuthToken', 'purchaseToken', 'signedUrl',
  'invoiceUrl',
]);

/** A credential that travels INSIDE a string rather than as a field of its
 *  own, so `REDACT_LOG_FIELDS` cannot catch it: a PayPro test-mode checkout URL
 *  carries the test-mode key as its `secret-key=` query parameter
 *  (df-59d822c22018). */
const SECRET_QUERY_PARAM_RE = /([?&]secret-key=)[^&#]*/gi;

/** Blanks the value of every secret query parameter in a string, keeping the
 *  parameter name so a log still shows that one was there. Used by the
 *  server's dispatch log and by the client before it logs a buy URL. */
export function redactSecretQueryParams(text: string): string {
  return text.replace(SECRET_QUERY_PARAM_RE, '$1<redacted>');
}

/** How many extra fields we are willing to spell out before we stop. This
 *  mainly governs the `Error` branch of `describeErrorForLog`, where the extra
 *  fields are what an HTTP client hung off the error. */
const MAX_DESCRIBED_FIELDS = 40;

/** Longest a single described value may be. An HTTP error routinely carries a
 *  whole HTML error page on `body`, and without this a log line — or, at
 *  `pay-ipn-handling.ts`, an HTTP response body — would carry all of it. */
const MAX_DESCRIBED_VALUE_CHARS = 512;

/** Longest the whole text may be. A backstop for the case the two caps above
 *  do not cover: many fields, each just under the per-value cap. */
const MAX_DESCRIBED_TOTAL_CHARS = 8000;

/** Longest a single console-line context may be. Deliberately far below
 *  `MAX_DESCRIBED_TOTAL_CHARS`: this one is paid on EVERY silent report, of
 *  which the app makes hundreds of kinds, and the whole point of the line is to
 *  stay readable in a log somebody is scrolling. Enough for a reason plus a
 *  couple of stack frames. */
const MAX_CONTEXT_LINE_CHARS = 1000;

/** How many `cause` links to follow. Deep enough for any real wrapping chain,
 *  shallow enough that a self-referencing `cause` cannot spin. */
const MAX_CAUSE_DEPTH = 5;

/**
 * A short, human-readable description of an unknown thrown value.
 *
 * Safe to put in a result object, an error message shown to a user, or a
 * telemetry property. It never includes a stack, so it is the version to use
 * for anything that crosses the wire.
 *
 * Order of preference: an `Error`'s own `message`; a plain object's `message`;
 * a CouchDB-style `error` + `reason` pair; the object's JSON; finally
 * `String(err)`.
 */
export function describeError(err: unknown): string {
  return truncate(describeErrorInner(err), MAX_DESCRIBED_TOTAL_CHARS);
}

function describeErrorInner(err: unknown): string {
  try {
    if (err instanceof Error) return err.message || err.name || 'Error';
    if (typeof err === 'string') return err;
    if (err && typeof err === 'object') {
      const e = err as { message?: unknown; error?: unknown; reason?: unknown };
      if (typeof e.message === 'string' && e.message) return e.message;
      // CouchDB and PouchDB reject with `{ status, error, reason }`
      if (typeof e.error === 'string' && typeof e.reason === 'string') return `${e.error}: ${e.reason}`;
      if (typeof e.error === 'string' && e.error) return e.error;
      // no recognisable message field — fall back to the object's own shape.
      // redact first: this string can reach a user, a webhook caller or Sentry.
      const json = safeJsonStringify(redactForDescribe(err));
      if (json !== undefined) return json;
      return '[unserializable error]';
    }
    return String(err);
  } catch {
    // a throwing getter, a revoked Proxy, or a `toString` that throws. Never
    // let the description of a failure become a second failure.
    return '[undescribable error]';
  }
}

/**
 * Everything we know about an unknown thrown value, for a LOG LINE ONLY.
 *
 * Never send this to a client and never put it in a result object — it can
 * contain a stack. Use `describeError` for that.
 *
 * This is deliberately built to be at least as informative as
 * `JSON.stringify(err)` for every possible input, so replacing a
 * `JSON.stringify` log call with this one can never lose detail:
 *
 *   - for an `Error`, the stack (which already begins with the message), plus
 *     any EXTRA own enumerable fields the thrower attached. HTTP clients hang
 *     the part you actually need — `statusCode`, `status`, `code`, `body` — off
 *     the error as extra fields, and the stack does not show them;
 *   - plus the `cause` chain. `cause` is non-enumerable, so it shows up in
 *     neither the stack nor the extra fields, and this codebase wraps errors
 *     with `{ cause: err }` in several places precisely to keep the underlying
 *     reason. Dropping it would throw away the very thing the wrapping saved;
 *   - for a plain object, the same full JSON dump `JSON.stringify` would give;
 *   - for anything else, the same text `describeError` gives.
 */
export function describeErrorForLog(err: unknown): string {
  return truncate(describeErrorForLogInner(err, 0), MAX_DESCRIBED_TOTAL_CHARS);
}

function describeErrorForLogInner(err: unknown, causeDepth: number): string {
  let text: string;
  try {
    if (err instanceof Error) {
      // a Node/V8 stack already starts with "Name: message", so it stands alone.
      // Browsers that omit the message from the stack get it prepended instead.
      const head = err.stack || `${err.name}: ${err.message}`;
      const extras = describeExtraErrorFields(err);
      text = extras ? `${head}\n  extra fields: ${extras}` : head;
    } else if (err && typeof err === 'object') {
      const json = safeJsonStringify(redactForDescribe(err), 2);
      if (json !== undefined) {
        text = json;
      } else {
        // circular, or otherwise unserializable — fall back to naming the fields
        const extras = describeExtraErrorFields(err);
        text = extras ? `[unserializable error] ${extras}` : '[unserializable error]';
      }
    } else {
      return describeErrorInner(err);
    }
  } catch {
    return '[undescribable error]';
  }

  // follow the `cause` chain. reading `.cause` is itself a property read that a
  // hostile object could make throw, so it is inside its own guard.
  if (causeDepth >= MAX_CAUSE_DEPTH) return `${text}\n  caused by: [cause chain too deep]`;
  let cause: unknown;
  try {
    cause = (err as { cause?: unknown })?.cause;
  } catch {
    return `${text}\n  caused by: [undescribable error]`;
  }
  if (cause === undefined || cause === null) return text;
  const causeText = describeErrorForLogInner(cause, causeDepth + 1);
  return `${text}\n  caused by: ${indentContinuationLines(causeText)}`;
}

/**
 * A value rendered for a console line that something else reads back LINE BY
 * LINE — as text, on ONE line, redacted, and short.
 *
 * Written for `LoggingService`'s silent-report line. That line used to pass its
 * context object as a second console ARGUMENT, and an object argument never
 * survives the trip into a log file: the E2E browser log is built from
 * Puppeteer's `msg.text()`, which renders a remote object as the literal text
 * `[object Object]`. In an E2E run that console line is the only channel there
 * is — Sentry is switched off for the hostname E2E drives — so every field a
 * caller put in the context was thrown away, and a failing scenario could show
 * the report's name and nothing else (df-2ff64a6cfd69).
 *
 * How it differs from `describeErrorForLog`, and why each difference is here:
 *
 *   - **one line.** The E2E logger writes one file line per console entry, and
 *     `boot-freeze-classifier` decides what a boot did by reading the log a line
 *     at a time. A multi-line payload would spread one report over several lines
 *     that carry none of its markers. Every newline becomes ` | ` instead.
 *   - **shorter.** `MAX_CONTEXT_LINE_CHARS`, not the 8000-character backstop.
 *     This runs on EVERY silent report, and there are hundreds of call sites.
 *   - **not only for errors.** A context is usually a plain bag of fields, so
 *     there is no message to prefer and no `cause` chain to follow — but a few
 *     callers do pass the caught error itself, and that case keeps its stack head
 *     by going through `describeErrorForLog`.
 *
 * Like everything else in this file it NEVER throws and it redacts credential
 * FIELDS by name at any depth. The same limit applies as everywhere here:
 * redaction cannot see inside an `Error`'s own `message` or `stack`.
 */
export function describeContextForLogLine(value: unknown): string {
  return truncate(foldOntoOneLine(describeContextForLogLineInner(value)), MAX_CONTEXT_LINE_CHARS);
}

function describeContextForLogLineInner(value: unknown): string {
  try {
    // a caller that passed the caught error itself keeps its stack head
    if (value instanceof Error) return describeErrorForLogInner(value, 0);
    if (value && typeof value === 'object') {
      // no indent: this has to stay on one line, and an indented dump would not
      const json = safeJsonStringify(redactForDescribe(value));
      if (json !== undefined) return json;
      // circular, or otherwise unserializable — fall back to naming the fields
      const extras = describeExtraErrorFields(value);
      return extras ? `[unserializable context] ${extras}` : '[unserializable context]';
    }
    if (typeof value === 'string') return value;
    return String(value);
  } catch {
    // a throwing getter, a revoked Proxy, or a `toString` that throws
    return '[undescribable context]';
  }
}

/** Every line after the first joined onto the first with ` | `, so a stack or a
 *  multi-line message cannot break the one-line-per-console-entry shape a log
 *  reader depends on. Blank lines are dropped rather than becoming empty
 *  segments. */
function foldOntoOneLine(text: string): string {
  return text
    .split('\n')
    .map(line => line.replace(/\r$/, '').trim())
    .filter(line => line.length > 0)
    .join(' | ');
}

/** Own enumerable fields of a thrown value, rendered as `name=value` pairs, or
 *  an empty string when there are none worth printing. Used to surface the
 *  fields an `Error`'s stack hides, and as the fallback when an object cannot
 *  be JSON-stringified at all. Never throws — a hostile or exotic thrown value
 *  must not turn a log call into a second failure. */
function describeExtraErrorFields(err: object): string {
  const parts: string[] = [];
  let keys: string[];
  try {
    keys = Object.keys(err);
  } catch {
    // a revoked Proxy throws on ownKeys
    return '[fields unreadable]';
  }
  for (const key of keys) {
    // `message` and `stack` are already covered by the stack line above
    if (key === 'message' || key === 'stack') continue;
    if (parts.length >= MAX_DESCRIBED_FIELDS) {
      parts.push('…');
      break;
    }
    if (REDACT_LOG_FIELDS.has(key)) {
      parts.push(`${key}=${redactedMarkerFor(readField(err, key))}`);
      continue;
    }
    parts.push(`${key}=${describeFieldValue(readField(err, key))}`);
  }
  return parts.join(' ');
}

/** Sentinel for a field whose own getter throws. A distinct object (not a
 *  string) so it can never be confused with a real value. */
const UNREADABLE_FIELD = Symbol('unreadable-field');

/** Reads one own property, turning a throwing getter into a sentinel rather
 *  than letting it escape. */
function readField(obj: object, key: string): unknown {
  try {
    return (obj as Record<string, unknown>)[key];
  } catch {
    return UNREADABLE_FIELD;
  }
}

/** One field of a thrown value, rendered compactly and length-capped. Nested
 *  objects go through JSON — redacted first — so a response body still shows
 *  its contents without showing a credential. */
function describeFieldValue(value: unknown): string {
  if (value === UNREADABLE_FIELD) return '[unreadable]';
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return truncate(value, MAX_DESCRIBED_VALUE_CHARS);
  if (typeof value === 'object') {
    const json = safeJsonStringify(redactForDescribe(value));
    return json === undefined ? '[unserializable]' : truncate(json, MAX_DESCRIBED_VALUE_CHARS);
  }
  try {
    return truncate(String(value), MAX_DESCRIBED_VALUE_CHARS);
  } catch {
    // a Symbol, or an object whose toString throws
    return '[undescribable]';
  }
}

/** What we print in place of a credential: its length, never its value. */
function redactedMarkerFor(value: unknown): string {
  if (typeof value === 'string') return `<redacted, ${value.length} chars>`;
  return '<redacted, non-string>';
}

/** How many values the redaction walk will visit before it gives up. The output
 *  caps below bound what gets PRINTED; this bounds the WORK done to produce it,
 *  because otherwise a thrown value carrying a big payload is fully deep-copied
 *  first and only then truncated — inside a `catch` block, whose whole job is
 *  to not make a bad situation worse.
 *
 *  Counts EVERY value the walk touches, not just the objects it descends into:
 *  a single flat array of a million strings costs nothing in depth and would
 *  sail past a container-only counter. */
const MAX_REDACTION_NODES = 5000;

/** A copy of the value with every credential field (see `REDACT_LOG_FIELDS`)
 *  replaced by a length-only marker, at any depth. Cycles are cut with a
 *  marker, and a field whose getter throws becomes a marker too, so this can
 *  be handed straight to `JSON.stringify`. */
function redactForDescribe(value: unknown): unknown {
  return redactForDescribeInner(value, new WeakSet(), 0, { visited: 0 });
}

/**
 * Values the walk must NOT copy key-by-key, and what to put in their place.
 * Returns `undefined` when the value is an ordinary object that should be
 * walked normally.
 *
 * Two cases, for two different reasons:
 *
 * - a `Date` has no own enumerable keys at all, so copying it entry-by-entry
 *   would turn it into `{}`. We substitute the formatted timestamp STRING
 *   rather than the `Date` object, which reads identically in the output but
 *   means nothing here is ever handed to `JSON.stringify` still able to control
 *   its own serialization — a `Date` subclass overriding `toJSON`, or a `Proxy`
 *   around one, would otherwise reopen the same hole the `toJSON` rule closes.
 * - binary (a Node `Buffer`, any TypedArray, a raw `ArrayBuffer`) is what HTTP
 *   clients hang off `err.body`. It is not an Array, so the walk would clone it
 *   one entry PER BYTE — a real memory spike inside a `catch` block. Even
 *   `Buffer`'s own `toJSON` is no good here: it emits the whole byte array. A
 *   length marker is what a debugger actually wants.
 *
 * Deliberately NOT "anything with a `toJSON`". That looks like the general
 * version of this rule and is a redaction hole: an object whose `toJSON`
 * returns `{ token: … }` would be handed to `JSON.stringify` un-walked, and the
 * credential would print. Only the known-safe shapes listed above skip the walk.
 *
 * Deliberately NOT an `Error` either, although a nested one walks to `{}` for
 * the same reason a `Date` does — `message` and `stack` are non-enumerable. A
 * substitution here would have to start a SECOND walk to keep the error's own
 * fields, and a second walk shares neither the cycle guard nor the budget of the
 * one already running, so `err.cause = err` would recurse until the stack blew.
 * The Error case is handled inside `redactForDescribeInner` instead, where both
 * of those live.
 */
function substituteForUnwalkableValue(value: object): { replacement: unknown } | undefined {
  if (value instanceof Date) {
    try {
      return { replacement: value.toISOString() };
    } catch {
      // an Invalid Date throws a RangeError from toISOString
      return { replacement: '<invalid date>' };
    }
  }
  try {
    if (ArrayBuffer.isView(value)) return { replacement: `<binary, ${(value as ArrayBufferView).byteLength} bytes>` };
    if (value instanceof ArrayBuffer) return { replacement: `<binary, ${value.byteLength} bytes>` };
  } catch {
    // a revoked Proxy throws on the instanceof / isView check
    return { replacement: '<unreadable>' };
  }
  return undefined;
}

/** `onPath` holds only the objects on the CURRENT recursion path — entries are
 *  removed on the way back up — so the same object appearing under two
 *  siblings is copied faithfully and only a true cycle becomes a marker.
 *  `budget` is shared across the whole walk, so it counts total work rather
 *  than per-branch depth. */
function redactForDescribeInner(
  value: unknown,
  onPath: WeakSet<object>,
  depth: number,
  budget: { visited: number },
): unknown {
  if (!value || typeof value !== 'object') return value;
  const substitute = substituteForUnwalkableValue(value);
  if (substitute) return substitute.replacement;
  if (depth > 20) return '<too deep>';
  if (budget.visited >= MAX_REDACTION_NODES) return '<too large>';
  if (onPath.has(value)) return '<cycle>';
  budget.visited += 1;
  onPath.add(value);
  let out: unknown;
  try {
    if (Array.isArray(value)) {
      const clone: unknown[] = [];
      for (const item of value) {
        // charge for EVERY item, primitives included — a flat array of a
        // million strings costs no depth and would otherwise be copied whole
        budget.visited += 1;
        if (budget.visited >= MAX_REDACTION_NODES) {
          clone.push('<too large>');
          break;
        }
        clone.push(redactForDescribeInner(item, onPath, depth + 1, budget));
      }
      out = clone;
    } else {
      const clone: Record<string, unknown> = {};
      // An Error reached as a FIELD of something else. `message` and `stack` are
      // non-enumerable, so the loop below cannot see them and this would walk to
      // `{}` — the trap this file's header describes, one level down. Seed the
      // message here and let the loop add the error's own enumerable fields as
      // it would for any object, so a nested error keeps everything the walk
      // printed before AND gains the message. It is seeded rather than
      // substituted so that it stays inside THIS walk: the cycle guard and the
      // budget above are what stop `err.cause = err` from spinning. The stack is
      // deliberately left out — this walk also feeds `describeError`, whose
      // output may cross the wire.
      // A top-level Error never reaches here: `describeError` and
      // `describeErrorForLog` both return from their own Error branch first.
      if (value instanceof Error) {
        clone['message'] = describeError(value);
      }
      for (const key of Object.keys(value)) {
        // charge for every FIELD, for the same reason as the array items above
        budget.visited += 1;
        if (budget.visited >= MAX_REDACTION_NODES) {
          clone['…'] = '<too large>';
          break;
        }
        // NEVER carry a `toJSON` across. `JSON.stringify` calls `toJSON` on
        // whatever object it is handed, so copying the function onto the clone
        // hands control back to the original value and defeats this entire
        // walk: an object whose `toJSON` returns `{ token: … }` would print the
        // credential despite every field here having been redacted.
        if (key === 'toJSON') {
          clone[key] = '<omitted>';
          continue;
        }
        const field = readField(value, key);
        if (field === UNREADABLE_FIELD) {
          clone[key] = '<unreadable>';
        } else if (REDACT_LOG_FIELDS.has(key)) {
          clone[key] = redactedMarkerFor(field);
        } else {
          clone[key] = redactForDescribeInner(field, onPath, depth + 1, budget);
        }
      }
      out = clone;
    }
  } catch {
    out = '<unreadable>';
  }
  onPath.delete(value);
  return out;
}

/** Cuts text to `max` characters, saying how much was dropped so a reader knows
 *  the line is not the whole story. */
function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max)}… [${text.length - max} more chars]`;
}

/** Indents every line after the first, so a nested cause reads as nested
 *  rather than running flush against the outer error's stack. */
function indentContinuationLines(text: string): string {
  return text.split('\n').join('\n  ');
}

/** `JSON.stringify` that returns `undefined` instead of throwing. A log call
 *  must never be able to crash the code path it is reporting on, and a circular
 *  object (or a `BigInt` field) makes plain `JSON.stringify` throw. */
function safeJsonStringify(value: unknown, indent?: number): string | undefined {
  try {
    const json = JSON.stringify(value, undefined, indent);
    // `JSON.stringify(undefined)` and a value whose `toJSON` returns undefined
    // both produce `undefined` rather than a string
    return typeof json === 'string' ? json : undefined;
  } catch {
    return undefined;
  }
}
